import * as React from 'react'
import { Maximize2 } from 'lucide-react'
import { fetchTabularPageImage } from '@axilon-apps/domains-ontology-ui-state'
import { SourceDocViewer } from '../../../components/SourceDocViewer'
import { ToggleGroup, ToggleGroupItem } from '../../../components/ui/toggle-group'
import { cn } from '../../../lib/utils'

type ImageVariant = 'original' | 'preprocessed'

/**
 * A scanned page's stored image, with an Original / Preprocessed switch when
 * the run kept both. Clicking the image opens it in the document preview.
 * Give it a `key` per page so the switch starts on Original for each one.
 */
export function PageImageViewer({
  imageUrl,
  preprocessedImageUrl,
  fileName,
  pageNumber,
  missingText = 'Blank page; no image retained.'
}: {
  imageUrl?: string
  preprocessedImageUrl?: string
  fileName: string
  pageNumber: number
  /** Shown in place of the image when there is none to fetch. */
  missingText?: string
}) {
  const [chosen, setChosen] = React.useState<ImageVariant>('original')
  const variant = preprocessedImageUrl ? chosen : 'original'
  const label = `${fileName} page ${pageNumber}`
  return (
    <div className="flex min-w-0 flex-col gap-2">
      {preprocessedImageUrl ? (
        <ToggleGroup
          type="single"
          variant="outline"
          size="sm"
          value={variant}
          onValueChange={(value) => value && setChosen(value as ImageVariant)}
          aria-label="Page image"
        >
          <ToggleGroupItem value="original">Original</ToggleGroupItem>
          <ToggleGroupItem value="preprocessed">Preprocessed</ToggleGroupItem>
        </ToggleGroup>
      ) : null}
      <PageImage
        imageUrl={variant === 'preprocessed' ? preprocessedImageUrl : imageUrl}
        label={variant === 'preprocessed' ? `${label} (preprocessed)` : label}
        fileName={fileName}
        pageNumber={pageNumber}
        preprocessed={variant === 'preprocessed'}
        missingText={missingText}
      />
      {preprocessedImageUrl ? (
        <p className="text-11 text-ink-3">The vision call read the preprocessed image.</p>
      ) : null}
    </div>
  )
}

function PageImage({
  imageUrl,
  label,
  fileName,
  pageNumber,
  preprocessed,
  missingText
}: {
  imageUrl?: string
  label: string
  fileName: string
  pageNumber: number
  preprocessed: boolean
  missingText: string
}) {
  const [image, setImage] = React.useState<{ blob: Blob; src: string }>()
  const [error, setError] = React.useState(false)
  const [enlarged, setEnlarged] = React.useState(false)
  React.useEffect(() => {
    let active = true
    let objectUrl: string | undefined
    setImage(undefined)
    setError(false)
    if (!imageUrl) return
    void fetchTabularPageImage(imageUrl)
      .then((blob) => {
        if (!active) return
        objectUrl = URL.createObjectURL(blob)
        setImage({ blob, src: objectUrl })
      })
      .catch(() => {
        if (active) setError(true)
      })
    return () => {
      active = false
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [imageUrl])
  const blob = image?.blob
  const loadSource = React.useCallback(async () => {
    if (!blob) throw new Error('The page image has not loaded.')
    return { blob, contentType: blob.type || 'image/png' }
  }, [blob])
  if (!imageUrl) return <div className="text-xs text-ink-3">{missingText}</div>
  if (error)
    return (
      <div role="alert" className="text-xs text-st-down">
        Page image unavailable.
      </div>
    )
  if (!image) return <div className="text-xs text-ink-3">Loading page image…</div>
  return (
    <>
      <button
        type="button"
        aria-label={`Open ${label} larger`}
        title="Open larger"
        className="group relative block w-full cursor-zoom-in rounded"
        onClick={() => setEnlarged(true)}
      >
        <img
          src={image.src}
          alt={label}
          className="max-h-[70vh] w-full rounded border object-contain"
        />
        <span className="pointer-events-none absolute top-2 right-2 rounded bg-background/85 p-1 text-ink-2 opacity-0 shadow-xs transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
          <Maximize2 className="size-3.5" aria-hidden />
        </span>
      </button>
      <SourceDocViewer
        open={enlarged}
        onOpenChange={setEnlarged}
        sourceFile={fileName}
        sourcePage={pageNumber}
        loadSource={loadSource}
        supportsOriginalDownload={false}
        {...(preprocessed ? { note: 'The preprocessed image the vision call read.' } : {})}
      />
    </>
  )
}

const ATTENTION_STATUSES: ReadonlySet<string> = new Set(['needs_review', 'unreadable', 'mismatch'])

/** A page status a person should look at: the model was unsure, or the page did not fit the config. */
export function pageNeedsAttention(status: string): boolean {
  return ATTENTION_STATUSES.has(status)
}

export function PageStatusChip({ status }: { status: string }) {
  return (
    <span
      className={cn(
        'rounded border px-1 py-0.5 text-10 uppercase whitespace-nowrap',
        pageNeedsAttention(status) ? 'border-st-warn/50 text-st-warn' : 'border-line text-ink-3'
      )}
    >
      {status.replace('_', ' ')}
    </span>
  )
}

export function percent(value: number): string {
  return `${Math.round(value * 100)}%`
}
